/**
 * Progreso en vivo de una corrida (§7.1).
 *
 * El backend no bloquea el request: devuelve un id y aquí se sondea cada dos
 * segundos hasta que la corrida termina. Se muestra la fase en curso
 * («consultando fuentes», «deduplicando») y el tiempo transcurrido, porque una
 * espera de varios minutos sin señal se percibe como una app colgada.
 */

import { CheckCircle2, CircleAlert, Loader2, TriangleAlert, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useCorrida } from '../api/hooks'
import type { Corrida, EstadoCorrida } from '../api/tipos'
import { Boton, Panel } from '../ui/primitivos'
import { cx, formatearCosto } from '../ui/util'

/** Fases esperadas, en orden. Sirven para dibujar la barra de avance. */
const FASES = ['en cola', 'consultando fuentes', 'normalizando y deduplicando', 'listo']

const FASES_PROFUNDIZACION = [
  'en cola',
  'resumiendo lo ya conocido',
  'consultando fuentes',
  'integrando novedades',
  'listo',
]

function transcurrido(desde: string | null): string {
  if (!desde) return ''
  const segundos = Math.max(0, Math.floor((Date.now() - new Date(desde).getTime()) / 1000))
  const min = Math.floor(segundos / 60)
  return min > 0 ? `${min} min ${segundos % 60} s` : `${segundos} s`
}

const ICONO: Record<EstadoCorrida, React.ReactNode> = {
  PENDIENTE: <Loader2 size={16} className="animate-spin" />,
  EN_CURSO: <Loader2 size={16} className="animate-spin" />,
  OK: <CheckCircle2 size={16} className="text-[var(--color-favorable)]" />,
  PARCIAL: <TriangleAlert size={16} className="text-[var(--color-critica)]" />,
  ERROR: <CircleAlert size={16} className="text-[var(--color-opositora)]" />,
}

const TITULO: Record<EstadoCorrida, string> = {
  PENDIENTE: 'En cola',
  EN_CURSO: 'Investigando',
  OK: 'Investigación completada',
  PARCIAL: 'Completada parcialmente',
  ERROR: 'La investigación falló',
}

function BarraDeFases({ corrida }: { corrida: Corrida }) {
  const fases = corrida.tipo === 'PROFUNDIZACION' ? FASES_PROFUNDIZACION : FASES
  const actual = fases.indexOf(corrida.fase)
  const indice = actual === -1 ? 0 : actual

  return (
    <ol className="mt-3 grid gap-1.5">
      {fases.slice(0, -1).map((fase, i) => {
        const hecha = i < indice || corrida.terminada
        const enCurso = i === indice && !corrida.terminada
        return (
          <li key={fase} className="flex items-center gap-2 text-[12px]">
            <span
              className={cx(
                'size-1.5 shrink-0 rounded-full',
                hecha
                  ? 'bg-[var(--color-favorable)]'
                  : enCurso
                    ? 'pulso bg-[var(--color-acento)]'
                    : 'bg-[var(--color-borde-fuerte)]',
              )}
            />
            <span
              className={cx(
                hecha || enCurso
                  ? 'text-[var(--color-texto)]'
                  : 'text-[var(--color-texto-tenue)]',
                enCurso && 'font-medium',
              )}
            >
              {fase.charAt(0).toUpperCase() + fase.slice(1)}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

export function ProgresoCorrida({
  corridaId,
  alTerminar,
  alCancelar,
  compacto = false,
}: {
  corridaId: number
  alTerminar?: (corrida: Corrida) => void
  alCancelar?: () => void
  compacto?: boolean
}) {
  const { data: corrida, isError, error } = useCorrida(corridaId)
  const [, forzarRender] = useState(0)

  // El cronómetro corre por su cuenta: el sondeo es cada 2 s pero el tiempo
  // transcurrido tiene que avanzar cada segundo para que se vea vivo.
  useEffect(() => {
    if (corrida?.terminada) return
    const id = setInterval(() => forzarRender((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [corrida?.terminada])

  useEffect(() => {
    if (corrida?.estado === 'OK' || corrida?.estado === 'PARCIAL') {
      alTerminar?.(corrida)
    }
  }, [corrida?.estado, corrida, alTerminar])

  if (isError) {
    return (
      <Panel titulo="Investigación">
        <p className="p-3 text-[13px] text-[var(--color-opositora)]">
          {error instanceof Error ? error.message : 'No se pudo consultar el estado.'}
        </p>
      </Panel>
    )
  }

  if (!corrida) {
    return (
      <Panel titulo="Investigación">
        <div className="flex items-center gap-2 p-3 text-[13px] text-[var(--color-texto-suave)]">
          <Loader2 size={14} className="animate-spin" /> Preparando la corrida…
        </div>
      </Panel>
    )
  }

  const ingesta = corrida.parametros?.ingesta

  return (
    <Panel
      titulo={
        <span className="inline-flex items-center gap-2">
          {ICONO[corrida.estado]}
          {TITULO[corrida.estado]}
        </span>
      }
      acciones={
        alCancelar && !corrida.terminada ? (
          <Boton
            variante="sutil"
            icono={<X size={14} />}
            onClick={alCancelar}
            title="Dejar de mostrar el progreso. La corrida sigue en el servidor."
          >
            Ocultar
          </Boton>
        ) : undefined
      }
      className="aparecer"
    >
      <div className="p-3">
        {!corrida.terminada && (
          <>
            <p className="text-[13px] text-[var(--color-texto-suave)]">
              {corrida.fase || 'Preparando…'} ·{' '}
              <span className="tabular">{transcurrido(corrida.creado_en)}</span>
            </p>
            <BarraDeFases corrida={corrida} />
            {!compacto && (
              <p className="mt-3 text-[12px] text-[var(--color-texto-tenue)]">
                Puedes cerrar esta página: la corrida sigue en el servidor y el resultado queda
                guardado.
              </p>
            )}
          </>
        )}

        {corrida.terminada && corrida.estado !== 'ERROR' && ingesta && (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-[13px] sm:grid-cols-4">
            {[
              ['Stakeholders', ingesta.creados],
              ['Fusionados', ingesta.fusionados],
              ['Relaciones', ingesta.relaciones_creadas + ingesta.relaciones_radiales],
              ['Fuentes', ingesta.fuentes_creadas],
            ].map(([etiqueta, valor]) => (
              <div key={etiqueta as string}>
                <dt className="text-[11px] text-[var(--color-texto-tenue)]">{etiqueta}</dt>
                <dd className="tabular font-medium">{valor}</dd>
              </div>
            ))}
          </dl>
        )}

        {corrida.estado === 'ERROR' && (
          <div className="text-[13px] text-[var(--color-opositora)]">
            <p>{corrida.error_mensaje || 'La corrida falló sin detalle.'}</p>
            {/* El texto crudo de la excepción queda a un clic: es lo que hace
                falta para reportar el fallo, pero no lo primero que se lee. */}
            {corrida.error && corrida.error !== corrida.error_mensaje && (
              <details className="mt-1.5">
                <summary className="cursor-pointer text-[12px] text-[var(--color-texto-tenue)]">
                  Detalle técnico
                </summary>
                <pre className="scroll-sutil mt-1 max-h-32 overflow-auto rounded border border-[var(--color-borde)] bg-[var(--color-lienzo)] p-2 text-[11px] whitespace-pre-wrap text-[var(--color-texto-suave)]">
                  {corrida.error}
                </pre>
              </details>
            )}
          </div>
        )}

        {corrida.terminada && (
          <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[var(--color-texto-tenue)]">
            <span>
              Duración{' '}
              <span className="tabular">
                {corrida.duracion_segundos ? `${Math.round(corrida.duracion_segundos)} s` : '—'}
              </span>
            </span>
            <span>
              Costo <span className="tabular">{formatearCosto(corrida.costo_usd)}</span>
            </span>
            {corrida.modelo && <span>Modelo {corrida.modelo}</span>}
          </p>
        )}

        {corrida.warnings.length > 0 && (
          <details className="mt-3">
            <summary className="cursor-pointer text-[12px] text-[var(--color-texto-suave)]">
              {corrida.warnings.length} aviso{corrida.warnings.length === 1 ? '' : 's'} de la
              corrida
            </summary>
            <ul className="scroll-sutil mt-2 max-h-52 list-disc overflow-y-auto pl-5 text-[12px] text-[var(--color-texto-suave)]">
              {corrida.warnings.map((aviso, i) => (
                <li key={i} className="py-0.5">
                  {aviso}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </Panel>
  )
}
